<template>
  <div class="block">
    <div
      v-if="isDraggable || title || subtitle || counter || !!$slots['header-content']"
      class="block-heading">
      <div
        v-if="isDraggable"
        class="handler"></div>

      <h2
        v-if="title"
        :title="titleAttribute">
        {{ title }}
        <strong
          v-if="subtitle"
          :class="{ [subtitleClass]: true }">
          {{ subtitle }}

          <i
            v-if="subtitleTooltip"
            class="item-info"
            @click.stop.prevent="showInfo('block-info-' + title)">
            <template v-if="subtitleClass === 'danger-text'">!</template>
            <template v-else>?</template>
            <info-box
              :ref="'block-info-' + title"
              :spacing="32"
              position="bottom">
              <div slot="content" v-pure-html="subtitleTooltip"></div>
            </info-box>
          </i>
        </strong>
      </h2>

      <counter
        v-if="counter !== false"
        :value="counter" />

      <slot name="header-content" />
    </div>

    <div :class="{ 'block-content': true, 'no-padding': noPadding }">
      <slot name="content" />
      <div :class="{ 'block-loader': true, 'is-visible': isLoading }">
        <span class="block-loader-text">{{ $t('common.block.loadingData') }}</span>
      </div>

      <div :class="{ 'block-save': true, 'is-visible': isSaving }">
        <span class="block-save-text">{{ $t('common.block.savingData') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'block',
  props: {
    isDraggable: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isSaving: {
      type: Boolean,
      default: false
    },
    counter: {
      type: [Number, Boolean],
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    titleAttribute: {
      type: [String, Number],
      default: ''
    },
    subtitle: {
      type: String,
      default: ''
    },
    subtitleClass: {
      type: String,
      default: 'default-text'
    },
    subtitleTooltip: {
      type: String,
      default: ''
    },
    noPadding: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    showInfo (id) {
      this.$bus.$emit('body-clicked');

      setTimeout(() => {
        if (this.$refs[id][0]) {
          this.$refs[id][0].show();
        } else if (this.$refs[id]) {
          this.$refs[id].show();
        }
      }, 0);
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.block {
  background: $color-light;
  border: 1px solid $color-border-light;
  border-radius: $border-radius-small;
  margin: 2.4rem auto;
  max-width: 1400px;

  h2 {
    font-size: 2rem;
    font-weight: 400;
    margin: 0;
    padding: 2rem 0;
    width: auto;

    strong {
      font-weight: normal;

      .item-info {
        align-items: center;
        background: $color-link;
        border-radius: 10px;
        color: $color-light;
        cursor: pointer;
        display: inline-flex;
        font-family: serif;
        font-size: 17px;
        font-style: normal;
        font-weight: bold;
        justify-content: center;
        height: 20px;
        position: relative;
        top: -1px;
        width: 20px;

        /deep/ .info-box-content {
          color: $color-text;
          font-size: 14px;
          font-family: sans-serif;
          font-weight: normal;
          padding: 1.2rem;
          text-align: center;
        }
      }

      &.danger-text {
        .item-info {
          background: $color-button-danger-top;
        }
      }

      &.default-text {
        color: $color-notice;
      }

      &.danger-text {
        color: $color-danger;
      }

      &.success-text {
        color: $color-success;
      }

      &.category-name {
        color: $color-text-light;
        font-size: 1.4rem;
        margin-left: 1rem;

        &:before {
          content: "(";
        }

        &:after {
          content: ")";
        }
      }

      &.is-new-line {
        display: block;
        font-size: 14px;
        margin-top: 5px;
      }
    }
  }

  &-heading {
    align-items: center;
    border-bottom: 1px solid $color-border-light;
    display: flex;
    padding: 0 3rem;
    position: relative;

    .counter {
      float: left;
    }
  }

  &-content {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    padding: 3.5rem 2.5rem;
    position: relative;

    &.no-padding {
      padding: 0;
    }
  }

  .handler {
    border-bottom: 2px solid $color-border;
    border-top: 2px solid $color-border;
    cursor: move;
    height: 2rem;
    left: 1.2rem;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 1rem;

    &:before {
      border-bottom: 2px solid $color-border;
      border-top: 2px solid $color-border;
      content: "";
      display: block;
      height: .8rem;
      margin-top: .4rem;
      width: 1rem;
    }
  }

  &-save,
  &-loader {
    background: rgba(255, 255, 255, .95);
    height: 100%;
    left: 0;
    opacity: 0;
    position: absolute;
    pointer-events: none;
    top: 0;
    transition: opacity .5s ease-out;
    width: 100%;

    &.is-visible {
      opacity: 1;
      pointer-events: auto;
    }

    &-text {
      color: $color-text-light;
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translateX(-50%) translateY(-50%);

      &:before {
        animation: spinner 1s infinite linear;
        border: 3px solid $color-text-lightest;
        border-left: 3px solid transparent;
        border-radius: 50%;
        content: "";
        display: block;
        height: 12px;
        left: -20px;
        position: absolute;
        top: 4px;
        width: 12px;
      }
    }
  }
}

@keyframes spinner {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1680px) {
  .block {
    margin: 2.4rem auto;
    max-width: 1400px;
  }
}

@media (max-width: 1480px) {
  .block {
    margin: 2.4rem 2rem;
    max-width: calc(100% - 4rem);
  }
}

@media (max-width: 1240px) {
  .block-heading {
    padding: 0 1rem;
  }

  .block-content {
    padding: 1rem;
  }
}

@media (max-width: 640px) {
  .block {
    margin: 1.4rem 1rem;
    max-width: calc(100% - 2rem);

    &-heading {
      flex-wrap: wrap;
    }

    .block-list-heading {
      display: none;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/common/Block.vue